import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  DesignTestInputDto,
  DesignTestOutputDto
} from '../../../models/job-request-iterations-dto.interface';

interface WbmChloridesReportValues {
  agNo3Concentration?: number;
  agNo3Volume?: number;
  bariteLbPerBbl?: number;
  diluentTitratedVolume?: number;
  flaskVolume?: number;
  mudSampleWeight?: number;
  bariteSharePoundsPerBarrel?: number;
  chloridesInBlend?: number;
  totalChloridesGml?: number;
  volumeOfDiluentTitratedMl?: number;
  volumeOfFlaskMl?: number;
}

@Component({
  selector: 'app-wbm-chlorides-test-report',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './wbm-chlorides-test-report.component.html',
  styleUrl: './wbm-chlorides-test-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WbmChloridesTestReportComponent {
  @Input({ required: true }) test!: DesignTestInputDto;
  @Input({ required: true }) output!: DesignTestOutputDto;

  protected wbmChloridesValues(): WbmChloridesReportValues | null {
    const values = this.derivedValues();
    const agNo3Concentration = this.numberValue(['agNo3Concentration', 'silverNitrateConcentration', 'wholeMudSilverNitrateConcentration'], this.output) ?? this.numberValue(['agNo3Concentration', 'silverNitrateConcentration'], this.test) ?? values.agNo3Concentration;
    const agNo3Volume = this.numberValue(['agNo3Volume', 'silverNitrateVolume', 'wholeMudSilverNitrateVolume'], this.output) ?? this.numberValue(['agNo3Volume', 'silverNitrateVolume'], this.test) ?? values.agNo3Volume;
    const bariteLbPerBbl = this.numberValue(['bariteLbPerBbl', 'bariteSharePoundsPerBarrel'], this.output) ?? this.numberValue(['bariteLbPerBbl', 'bariteSharePoundsPerBarrel'], this.test) ?? values.bariteLbPerBbl;
    const diluentTitratedVolume = this.numberValue(['diluentTitratedVolume', 'volumeOfDiluentTitratedMl'], this.output) ?? this.numberValue(['diluentTitratedVolume', 'volumeOfDiluentTitratedMl'], this.test) ?? values.diluentTitratedVolume;
    const flaskVolume = this.numberValue(['flaskVolume', 'volumeOfFlaskMl'], this.output) ?? this.numberValue(['flaskVolume', 'volumeOfFlaskMl'], this.test) ?? values.flaskVolume;
    const mudSampleWeight = this.numberValue(['mudSampleWeight', 'wholeMudSampleWeight', 'mudSampleVolume', 'wholeMudSampleVolume', 'sampleWeight'], this.output) ?? this.numberValue(['mudSampleWeight', 'wholeMudSampleWeight'], this.test) ?? values.mudSampleWeight;
    const chloridesInBlend = this.numberValue(['chloridesInBlend', 'wbmTitration_chloridesInBlend'], this.output) ?? values.chloridesInBlend;
    const totalChloridesGml = this.numberValue(['totalChloridesGml', 'wbmTitration_totalChlorides', 'totalChlorides'], this.output) ?? values.totalChloridesGml;
    const volumeOfDiluentTitratedMl = this.numberValue(['volumeOfDiluentTitratedMl', 'diluentTitratedVolume'], this.output) ?? values.volumeOfDiluentTitratedMl ?? diluentTitratedVolume;
    const volumeOfFlaskMl = this.numberValue(['volumeOfFlaskMl', 'flaskVolume'], this.output) ?? values.volumeOfFlaskMl ?? flaskVolume;

    if ([agNo3Concentration, agNo3Volume, bariteLbPerBbl, diluentTitratedVolume, flaskVolume, mudSampleWeight, totalChloridesGml].every(v => v === undefined)) return null;

    return {
      agNo3Concentration,
      agNo3Volume,
      bariteLbPerBbl,
      diluentTitratedVolume,
      flaskVolume,
      mudSampleWeight,
      chloridesInBlend,
      totalChloridesGml,
      volumeOfDiluentTitratedMl,
      volumeOfFlaskMl,
      bariteSharePoundsPerBarrel: bariteLbPerBbl
    };
  }

  protected formatDecimal(value: number | undefined): string {
    return value === undefined ? '—' : value.toFixed(2);
  }

  protected formatWholeNumber(value: number | undefined): string {
    return value === undefined ? '—' : new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);
  }

  private derivedValues(): WbmChloridesReportValues {
    // Attempt to derive common fields from parsed XML or inputs/outputs when not explicitly provided
    const agNo3Concentration = this.firstNumber(['agNo3Concentration', 'silverNitrateConcentration', 'wholeMudSilverNitrateConcentration'], this.output, this.test);
    const agNo3Volume = this.firstNumber(['agNo3Volume', 'silverNitrateVolume', 'wholeMudSilverNitrateVolume'], this.output, this.test);
    const diluentTitratedVolume = this.firstNumber(['diluentTitratedVolume', 'volumeOfDiluentTitratedMl'], this.output, this.test);
    const flaskVolume = this.firstNumber(['flaskVolume', 'volumeOfFlaskMl'], this.output, this.test);
    const mudSampleWeight = this.firstNumber(['mudSampleWeight', 'wholeMudSampleWeight', 'sampleWeight', 'mudSampleVolume'], this.output, this.test);
    const bariteLbPerBbl = this.firstNumber(['bariteLbPerBbl', 'bariteSharePoundsPerBarrel'], this.output, this.test);
    const chloridesInBlend = this.firstNumber(['chloridesInBlend', 'wbmTitration_chloridesInBlend'], this.output, this.test);
    const totalChloridesGml = this.firstNumber(['totalChloridesGml', 'wbmTitration_totalChlorides', 'totalChlorides'], this.output, this.test);

    return {
      agNo3Concentration,
      agNo3Volume,
      diluentTitratedVolume,
      flaskVolume,
      mudSampleWeight,
      bariteLbPerBbl,
      chloridesInBlend,
      totalChloridesGml
    };
  }

  private firstNumber(names: string[], ...sources: Array<DesignTestInputDto | DesignTestOutputDto>): number | undefined {
    for (const source of sources) {
      const v = this.numberValue(names, source);
      if (v !== undefined) return v;
    }
    return undefined;
  }

  private numberValue(names: string[], source: DesignTestInputDto | DesignTestOutputDto): number | undefined {
    const v = this.value(names, source);
    return this.toNumber(v);
  }

  private value(names: string[], source: DesignTestInputDto | DesignTestOutputDto): string | null {
    const accepted = new Set(names.map(n => this.normalize(n)));
    const fields = 'parsedValues' in source ? source.parsedValues : source.parsedInputValues;
    const parsed = fields.find(f => accepted.has(this.normalize(f.label)));
    return parsed?.value?.trim() || this.xmlValue(source.content, accepted);
  }

  private xmlValue(content: string | null, names: Set<string>): string | null {
    if (!content) return null;
    const doc = new DOMParser().parseFromString(content, 'application/xml');
    if (doc.querySelector('parsererror')) return null;
    for (const el of Array.from(doc.getElementsByTagName('*'))) {
      if (el.localName.toLowerCase() === 'parameter') {
        if (!names.has(this.normalize(el.getAttribute('key')))) continue;
      } else if (!names.has(this.normalize(el.localName))) continue;
      if (el.children.length === 0) {
        const t = el.textContent?.trim();
        if (t) return t;
      }
    }
    return null;
  }

  private normalize(value: string | null | undefined): string {
    return (value ?? '').replace(/[\s_\-/,().:%]+/g, '').toLowerCase();
  }

  private toNumber(value: string | null): number | undefined {
    if (!value) return undefined;
    const parsed = Number(value.replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : undefined;
  }
}
